<template>
  <el-card class="mb-2 mt-1 promotion-code-item" :class="[
    isDisabled ? 'is-disabled' : '',
    codeState.data?.id === item?.id ? 'is-selected' : '',
    !isDisabled && codeState.data?.id !== item?.id ? 'is-hover' : '',
  ]" shadow="hover" @click="onClickPromo">
    <template #header>
      <el-alert v-if="validateCodeHint" :title="validateCodeHint" class="mb-1" type="warning" :closable="false"
        show-icon></el-alert>
      <el-alert v-else-if="!isSelected" :title="$t('符合使用條件')" class="mb-1" type="success" :closable="false"
        show-icon></el-alert>
      <div class="flex">
        <div class="text-accent">
          <el-icon class="mr-2" size="large" style="font-size: 40px;">
            <Ticket />
          </el-icon>
        </div>
        <div :style="{ flex: 1 }">
          <span class="block text-gray-600 text-lg font-weight-bold">
            {{
              dataTranslations(item?.promotion?.title_translations || {}) || ""
            }}
          </span>
          <div style="line-height: 0;">
            <div v-if="item?.promotion?.conditions && item.promotion.conditions.length"
              class="text-accent text-sm font-weight-bolder">
              <span v-if="item?.promotion?.discount_type == 'amount'">
                {{
                  `- ${codeState.currency}${currencyFormat(
                    item.promotion.conditions[0].discount_amount | 0
                  )}`
                }}
              </span>
              <span v-else-if="item?.promotion?.discount_type == 'percentage'">
                {{
                  `- ${currencyFormat(
                    item.promotion.conditions[0].discount_percentage | 0
                  )}%`
                }}
              </span>
              <span
                v-if="item?.promotion?.discount_type == 'percentage' && !!item.promotion.conditions[0].discount_amount">
                ({{ $t("promotions.maxAmount") }}
                {{ codeState.currency }}
                {{
                  currencyFormat(
                    item.promotion.conditions[0].discount_amount | 0
                  )
                }})
              </span>
            </div>
            <div v-if="!!item?.promotion?.max_use_count" class="text-gray-400">
              {{
                $t("promotions.promotionMaxUseLimit", [
                  currencyFormat(item.promotion.max_use_count || 0),
                ])
              }}
              <el-tag type="success" class="v-chip-remains-use-count text-accent" size="small" effect="light">
                {{
                  $t("promotions.promotionTotalRemaining", [
                    currencyFormat(
                      (item.promotion.max_use_count || 0) -
                      (item.promotion.use_count || 0)
                    ),
                  ])
                }}
              </el-tag>
            </div>
          </div>
          <span v-if="item?.promotion?.codes" class="block text-xs text-gray-400" :style="{ marginTop: '1px' }">
            <el-icon class="text-accent" size="small" :style="{ marginTop: '-2px' }">
              <Warning />
            </el-icon>
            {{ item.promotion.codes[0] }}
          </span>
        </div>
        <el-tag v-if="item?.promotion?.condition_scope == 'non' ||
          item?.promotion?.condition_scope == 'subtotal'
          " class="px-1" size="small" effect="plain" type="danger">
          {{ $t("promotions.subtotal") }}
        </el-tag>
        <el-tag v-else-if="item?.promotion?.condition_scope == 'categories'" effect="plain" size="small">
          {{ $t("promotions.categories") }}
        </el-tag>
        <el-tag v-else-if="item?.promotion?.condition_scope == 'products'" effect="plain" size="small">
          {{ $t("promotions.products") }}
        </el-tag>
        <el-tag v-else-if="item?.promotion?.condition_scope == 'promotions' ||
          item?.promotion?.condition_scope == 'promotion_groups'
          " effect="plain" size="small">
          {{ $t("promotions.promotions") }}
        </el-tag>
        <el-tag v-else effect="plain" size="small">
          {{ item?.promotion?.condition_scope }}
        </el-tag>
      </div>
    </template>
    <div class="justify-content-between align-items-center">
      <template v-if="isSelected && isValid">
        <div class="flex align-items-center text-accent">
          <el-icon class="font-weight-bolder" size="large">
            <CircleCheck />
          </el-icon>
          <span class="font-weight-bolder text-base">
            {{ $t("已使用折扣碼") }}
          </span>
        </div>
      </template>
      <template v-else>
        <el-tag size="small" class="text-gray-400 v-chip-border">
          <span v-if="item?.promotion?.conditions && item.promotion.conditions.length
            ">
            {{ $t("promotions.lowAmount") }}:
            {{ codeState.currency }}
            {{ currencyFormat(item.promotion.conditions[0].min_amount | 0) }}
          </span>
          <template v-if="item?.promotion?.ended_at">
            <span> ， </span>
            <span class="orange--text text--darken-2 font-weight-bold">
              {{
                `${$t("promotions.promotionCodeValidAt", [
                  `${dateFormat(item.promotion.ended_at).replaceAll("/", ".")}`,
                ])}`
              }}
            </span>
          </template>
        </el-tag>
      </template>
      <div class="text-right">
        <el-button v-if="isSelected" type="danger" size="small" @click="onClickRemove">
          {{ $t("刪除") }}
        </el-button>
        <el-button v-else type="primary" size="small" @click="onClickPromo">
          {{ $t("使用") }}
        </el-button>
      </div>
    </div>
  </el-card>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import {
  currencyFormat,
  dateFormat,
  dataTranslations,
} from "../../../utils";
import { Warning, Ticket, CircleCheck } from '@element-plus/icons-vue'

const props = defineProps({
  form: Object,
  item: Object,
  codeState: Object,
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(["click", "allowUse"]);

const isDisabled = computed(
  () => props.codeState.params.status == "past" || !props.item.is_valid
);

const onClickRemove = () => {
  emit("click", props.item);
};

const onClickPromo = () => {
  if (!props.isSelected) {
    emit("click", props.item);
  }
};

const isValid = ref(false);

const isValidDate = () => {
  const currItem = props.item;
  if (!currItem?.promotion?.start_at) {
    return false;
  }
  const unit = "minute";
  const startChk = dayjs().isSameOrAfter(currItem?.promotion?.start_at, unit);
  if (currItem?.promotion?.ended_at) {
    const endChk = dayjs().isBetween(
      dayjs(currItem?.promotion?.start_at),
      dayjs(currItem?.promotion?.ended_at),
      unit
    );
    // console.log("isBetween: ", endChk)
    return endChk;
  }
  return startChk;
};

const chkProductIds = (arr1, arr2) => {
  // 比對重複的值
  return arr1.filter((val) => arr2.includes(val));
};

const validateCodeHint = computed(() => {
  const currItem = props.item;
  const form = props.form;
  try {
    if (!currItem) {
      isValid.value = false;
      return "請先選擇折扣碼";
    }
    // 檢查使用期限
    if (!isValidDate()) {
      isValid.value = false;
      return "此折扣碼已過期或尚未開始";
    }
    // 檢查使用次數
    if (
      currItem?.promotion?.max_use_count &&
      currItem.promotion.max_use_count <= currItem.promotion.use_count
    ) {
      return "已無剩餘使用次數";
    }
    const conditionScope = currItem?.promotion?.condition_scope;
    // console.debug("conditionScope: ", conditionScope);
    const carts = form.carts.filter(
      (el) => el.checked && !el?.is_excluded_discount
    );
    // if (["categories", "products", "subtotal"].includes(conditionScope)) {
    //   carts = carts.filter((el) => el.checked && !el?.is_excluded_discount);
    // } else if (["promotions", "promotion_groups"].includes(conditionScope)) {
    //   carts = carts.filter((el) => el.checked );
    // }
    // console.debug("carts: ", carts);
    if (!carts?.length) {
      isValid.value = false;
      return "購物車中的商品不適用折扣碼";
    }
    let validAmount = 0;
    if (conditionScope == "categories") {
      // 指定分類
      let num = 0;
      carts.forEach((cart) => {
        const chkCat = chkProductIds(
          currItem.promotion?.conditions[0].whitelisted_store_cat_ids,
          cart.store_cat_ids
        );
        // console.log("chkCat: ", chkCat)

        // 檢查排除assort
        if (chkCat?.length) {
          if (
            currItem?.promotion?.conditions[0].blacklisted_store_assort_ids &&
            !!currItem.promotion?.conditions[0].blacklisted_store_assort_ids
              ?.length
          ) {
            if (
              !currItem.promotion?.conditions[0].blacklisted_store_assort_ids.includes(
                cart.store_assort_id
              )
            ) {
              num++;
              validAmount += cart.sub_total;
            }
          } else {
            num++;
            validAmount += cart.sub_total;
          }
        }
      });
      // console.log("num: ", num)
      if (!num) {
        isValid.value = false;
        return "購物車中的商品不適用此折扣碼";
      }
    } else if (conditionScope == "products") {
      // 指定商品
      let num = 0;
      carts.forEach((cart) => {
        if (
          currItem.promotion?.conditions[0].whitelisted_store_product_ids.includes(
            cart.store_product_id
          )
        ) {
          // 檢查排除assort
          if (
            currItem?.promotion?.conditions[0].blacklisted_store_assort_ids &&
            !!currItem.promotion?.conditions[0].blacklisted_store_assort_ids
              ?.length
          ) {
            if (
              !currItem?.promotion?.conditions[0].blacklisted_store_assort_ids.includes(
                cart.store_assort_id
              )
            ) {
              validAmount += cart.sub_total;
              num++;
            }
          } else {
            validAmount += cart.sub_total;
            num++;
          }
        }
      });
      if (!num) {
        isValid.value = false;
        return "購物車中的商品不適用此折扣碼";
      }
    } else if (conditionScope == "subtotal") {
      // 全站商品
      let num = 0;
      carts.forEach((cart) => {
        // 檢查排除assort
        if (
          currItem?.promotion?.conditions[0].blacklisted_store_assort_ids &&
          !!currItem.promotion?.conditions[0].blacklisted_store_assort_ids
            ?.length
        ) {
          if (
            !currItem?.promotion?.conditions[0].blacklisted_store_assort_ids.includes(
              cart.store_assort_id
            )
          ) {
            validAmount += cart.sub_total;
            num++;
          }
        } else {
          validAmount += cart.sub_total;
          num++;
        }
      });
      if (!num) {
        isValid.value = false;
        return "購物車中的商品不適用此折扣碼";
      }
    } else if (conditionScope == "promotions") {
      // 指定活動
      let num = 0;
      const whitelistedStorePromGroupIds =
        currItem.promotion?.conditions?.[0]?.whitelisted_store_prom_ids || [];
      carts.forEach((cart) => {
        if (
          whitelistedStorePromGroupIds.includes(cart?.event_group?.event_id)
        ) {
          validAmount += cart.sub_total;
          num++;
        }
      });
      if (!num) {
        isValid.value = false;
        return "購物車中的商品不適用此折扣碼";
      }
    } else if (conditionScope == "promotion_groups") {
      // 指定活動組
      let num = 0;
      const whitelistedStorePromGroupIds =
        currItem.promotion?.conditions?.[0]?.whitelisted_store_prom_group_ids ||
        [];

      const whitelistedStorePromUuids = Object.values(props.promotionMap)
        .filter((p) =>
          !!p?.parent_id
            ? whitelistedStorePromGroupIds.includes(p.parent_id)
            : whitelistedStorePromGroupIds.includes(p.id)
        )
        .map((p) => p.event_group_uuid);
      // console.debug("promotionMap", Object.values(props.promotionMap))

      carts.forEach((cart) => {
        if (
          whitelistedStorePromUuids.includes(
            cart?.event_group?.event_group_uuid
          )
        ) {
          validAmount += cart.sub_total;
          num++;
        }
      });
      if (!num) {
        isValid.value = false;
        return "購物車中的商品不適用此折扣碼";
      }
    }
    // 檢查最低消費
    if (validAmount < currItem?.promotion?.conditions[0].min_amount) {
      const amount = Number(
        currItem?.promotion?.conditions[0].min_amount - validAmount
      );
      isValid.value = false;
      return `再加入 ${props.codeState.currency}${currencyFormat(
        amount
      )} 以使用此折扣碼`;
    }
    isValid.value = true;
    return "";
  } catch (error) {
    isValid.value = false;
    return `${error}`;
  }
});

watch(
  () => isValid.value,
  (val) => {
    emit("allowUse", val);
  },
  { immediate: true }
);
</script>
<style lang="scss">
.promotion-code-item {
  &.is-hover {
    cursor: pointer;

    &:hover {
      border-color: var(--primary_color);

      .el-card__header {
        // background-color: var(--primary_color_light);
      }
    }
  }

  &.is-selected {
    border-color: var(--primary_color);

    .el-card__header {
      // background-color: var(----primary_color_light2);
    }
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .el-card__header {
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 10px;
    padding-bottom: 10px;

    .el-tag--plain {
      color: var(--primary_color);
      border-color: var(--primary_color);
    }
  }

  .el-card__body {
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .v-chip-remains-use-count {
    padding: 5px 5px;
    height: 22px;
    font-size: 12px;
  }
}
</style>